import { Stream } from 'types/stream';

import { getLumeoFileIdFromStreamUri } from 'services/stream';

import { useDeleteFiles } from 'files/hooks/useDeleteFiles';
import { useDeleteStreams } from 'streams/hooks/useDeleteStreams';

import { useDownloadFile } from 'files/hooks/useDownloadFile';
import { useModal } from 'hooks/useModal';

import {
  ContextMenu,
  ContextMenuAction,
  ContextMenuSeparator,
} from 'components/ContextMenu/ContextMenu';
import { IconButton } from 'components/IconButton/IconButtonDeprecated';

export type FileActionsProps = {
  file: Stream;
  onRenameClick: (fileId: string) => void;
};

export function FileActions({
  file: fileStream,
  onRenameClick,
}: FileActionsProps) {
  const { mutateAsync: deleteFiles } = useDeleteFiles();
  const { mutateAsync: deleteStreams } = useDeleteStreams();
  const { download } = useDownloadFile();

  const { open } = useModal();

  const isLinkedFile = fileStream.uri && !fileStream.uri.includes('lumeo://');
  const fileId = getLumeoFileIdFromStreamUri(fileStream);

  function handleRenameClick() {
    onRenameClick(fileStream.id);
  }

  function handleDownloadClick() {
    if (!fileId) {
      return;
    }

    download(fileId);
  }

  function handleCopyUrlClick() {
    if (!isLinkedFile) {
      return;
    }

    navigator.clipboard.writeText(fileStream.uri);
  }

  function handleDeleteClick() {
    if (fileId) {
      open('confirm-delete', {
        title: `Are you sure you want to delete ${fileStream.name}?`,
        onConfirm: () => deleteFiles({ file_ids: [fileId] }),
      });
      return;
    }

    open('confirm-delete', {
      title: `Are you sure you want to delete ${fileStream.name}?`,
      onConfirm: () => deleteStreams({ stream_ids: [fileStream.id] }),
    });
  }

  return (
    <ContextMenu
      trigger={
        <IconButton
          label="More actions"
          variant="ghost"
          icon="more-vertical"
          size="small"
        />
      }
    >
      <ContextMenuAction onClick={handleRenameClick} icon="edit">
        Rename
      </ContextMenuAction>

      {fileId ? (
        <ContextMenuAction onClick={handleDownloadClick} icon="download">
          Download
        </ContextMenuAction>
      ) : undefined}

      {isLinkedFile ? (
        <ContextMenuAction onClick={handleCopyUrlClick} icon="clipboard">
          Copy URL
        </ContextMenuAction>
      ) : undefined}

      <ContextMenuSeparator />

      <ContextMenuAction
        onClick={handleDeleteClick}
        intent="danger"
        icon="delete"
      >
        Delete
      </ContextMenuAction>
    </ContextMenu>
  );
}
